import React from "react"
import { classNames } from "../../../../utils/classNames"
import { Header, Table } from "@tanstack/react-table"
import { SortArrows } from "./SortArrows"
import { Maybe } from "@figurepos/fgr-lib-business-logic/lib/common/types"
import { TableColumnAlign, TableRowData, TableRowSelectVisibility, TableSize, TableStyle } from "../types"
import { mapSortDirection } from "../utils"
import { head } from "ramda"
import { Checkbox } from "antd"
import {
    flexRenderCustom,
    getCellHeightClasses,
    getFirstColButtonsWidth,
    getOuterColumnPaddingClasses,
} from "../styleUtils"

type Props<TRowData> = {
    size: TableSize
    style: TableStyle
    table: Table<TRowData>
    rowClassName?: string
    bordered?: boolean
    virtualized?: boolean
}

export const TableHeader = <TRowData extends TableRowData>(props: Props<TRowData>) => {
    const columnAlignToClass = (align: Maybe<TableColumnAlign>): string => {
        switch (align) {
            case "left":
                return "justify-start text-left"
            case "center":
                return "justify-center text-center"
            case "right":
                return "justify-end text-right"
            default:
                return "justify-start text-left"
        }
    }

    const renderHeaderCell = (header: Header<TRowData, unknown>) => {
        if (header.isPlaceholder) {
            return null
        }

        const alignClass = columnAlignToClass(header.column.columnDef.meta?.align)
        const isSortable = header.column.getCanSort()

        const isFirstColumn = head(props.table.getAllColumns())?.id === header.column.id
        const isSelectCheckboxCol = !!(isFirstColumn && props.table.options.meta?.hasSelectableRows)

        const selectVisibilityClass =
            props.table.options.meta?.rowSelectVisibility === TableRowSelectVisibility.Always ||
            props.table.getIsSomeRowsSelected() ||
            props.table.getIsAllRowsSelected()
                ? ""
                : "invisible"

        // Todo: we probably want to refactor whole table to use "grid" and "flex" elements
        // not just when virtualization is enabled, but always (so we can avoid such conditions).
        const WrapElement = props.virtualized ? React.Fragment : "div"

        const firstColButtonsWidth = getFirstColButtonsWidth(false, isSelectCheckboxCol, 0)

        return (
            <WrapElement className={!props.virtualized ? classNames(classes.headerWrap) : undefined}>
                {isFirstColumn && firstColButtonsWidth > 0 ? (
                    <div
                        className={classes.firstColumnLeftPadding}
                        style={{
                            width: getFirstColButtonsWidth(false, isSelectCheckboxCol, 0) + "px",
                        }}>
                        {isSelectCheckboxCol ? (
                            <Checkbox
                                className={selectVisibilityClass}
                                checked={props.table.getIsAllRowsSelected()}
                                indeterminate={
                                    !props.table.getIsAllRowsSelected() && props.table.getIsSomeRowsSelected()
                                }
                                onChange={props.table.getToggleAllRowsSelectedHandler()}
                            />
                        ) : null}
                    </div>
                ) : null}
                {isSortable ? (
                    <button
                        type="button"
                        className={classNames(classes.headerBtn, classes.sortableHeaderBtn, alignClass)}
                        onClick={header.column.getToggleSortingHandler()}>
                        {flexRenderCustom(header.column.columnDef.header, header.getContext())}
                        <div className={classes.sortWrap}>
                            <SortArrows sort={mapSortDirection(header.column.getIsSorted())} />
                        </div>
                    </button>
                ) : (
                    <div className={classNames(classes.headerBtn, alignClass)}>
                        {flexRenderCustom(header.column.columnDef.header, header.getContext())}
                    </div>
                )}
                {header.column.columnDef.meta?.filterComponent ? header.column.columnDef.meta.filterComponent() : null}
            </WrapElement>
        )
    }

    return (
        <thead className={classNames(classes.header[props.style], props.virtualized && classes.headerBaseVirtualized)}>
            {props.table.getHeaderGroups().map(headerGroup => (
                <tr
                    key={headerGroup.id}
                    className={classNames(
                        getOuterColumnPaddingClasses(props.size).headerAndFooter,
                        props.virtualized && classes.rowBaseVirtualized,
                        props.bordered && classes.bordered,
                        props.rowClassName
                    )}>
                    {headerGroup.headers.map(header => {
                        const alignClass = columnAlignToClass(header.column.columnDef.meta?.align)
                        return (
                            <th
                                className={classNames(
                                    classes.headerCell,
                                    getCellHeightClasses(props.size),
                                    props.virtualized && [classes.headerCellVirtualized, alignClass],
                                    header.column.columnDef.meta?.columnClassName
                                )}
                                style={{
                                    width: header.column.columnDef.meta?.widthPercentage ?? header.getSize(),
                                    maxWidth: header.column.columnDef.meta?.maxWidth,
                                }}
                                key={header.id}
                                colSpan={header.colSpan}>
                                {renderHeaderCell(header)}
                            </th>
                        )
                    })}
                </tr>
            ))}
        </thead>
    )
}

const classes = {
    headerWrap: "flex items-center h-full",
    header: {
        normal: "bg-gray-50",
        dashed: "bg-gray-50",
        legacy: "bg-[#F6F6F8]",
    } as Record<TableStyle, string>,
    headerCell: "text-black/80 text-14",
    headerCellVirtualized: "flex",
    headerBtn: "flex w-full items-center bg-transparent h-full py-0 m-0 leading-5 font-normal",
    sortableHeaderBtn: "hover:bg-gray-100/80 cursor-pointer",
    sortWrap: "mt-px pl-2",
    bordered: "[&>th]:border-r [&>th]:border-gray-200/80 [&>th:last-child]:border-r-0",
    firstColumnLeftPadding: "flex h-full items-center",
    rowBaseVirtualized: "w-full flex",
    headerBaseVirtualized: "w-full grid",
}
